import { useLocation } from 'wouter'
import { Sun, Moon, Monitor, Bell, Search, Menu } from 'lucide-react'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const PAGE_TITLES: Record<string, { title: string; description: string }> = {
  '/dashboard': { title: 'Dashboard', description: 'Platform overview and quick stats' },
  '/dashboard/keys': { title: 'API Keys', description: 'Manage your Web Crypto signed API keys' },
  '/dashboard/gateway': { title: 'AI Gateway', description: 'Configure providers and routing' },
  '/dashboard/database': { title: 'EdgeSQL D1', description: 'Browse and query your D1 database' },
  '/dashboard/analytics': { title: 'Analytics', description: 'Usage metrics and performance insights' },
  '/dashboard/storage': { title: 'R2 Storage', description: 'Manage R2 buckets and objects' },
  '/dashboard/docs': { title: 'Documentation', description: 'API reference and integration guides' },
  '/dashboard/settings': { title: 'Settings', description: 'Account and platform configuration' },
  '/dashboard/connectivity': { title: 'Connectivity', description: 'Network topology and worker bindings' },
}

interface HeaderProps {
  onMenuToggle?: () => void
}

export function Header({ onMenuToggle }: HeaderProps) {
  const [pathname] = useLocation()
  const { setTheme, theme } = useTheme()
  const page = PAGE_TITLES[pathname] ?? { title: 'LavaFlow', description: '' }

  return (
    <header className="h-14 border-b border-border bg-card/80 backdrop-blur-sm flex items-center gap-4 px-4 sticky top-0 z-40">
      {/* Mobile menu */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden flex-shrink-0 w-8 h-8"
        onClick={onMenuToggle}
      >
        <Menu className="w-4 h-4" />
      </Button>

      {/* Page title */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="text-sm font-semibold text-foreground truncate">{page.title}</h1>
          <Badge
            variant="outline"
            className="hidden sm:flex text-[9px] px-1.5 h-4 font-mono border-border text-muted-foreground"
          >
            v1
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground truncate hidden sm:block">{page.description}</p>
      </div>

      {/* Search */}
      <div className="hidden md:flex items-center relative">
        <Search className="absolute left-2.5 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Search..."
          className="pl-8 h-8 w-52 text-xs bg-muted/50 border-border focus-visible:ring-ring"
        />
        <kbd className="absolute right-2 text-[10px] text-muted-foreground font-mono bg-background border border-border rounded px-1 pointer-events-none">
          ⌘K
        </kbd>
      </div>

      {/* Notifications */}
      <Button variant="ghost" size="icon" className="w-8 h-8 relative flex-shrink-0">
        <Bell className="w-4 h-4" />
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-lava" />
        <span className="sr-only">Notifications</span>
      </Button>

      {/* Theme switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="w-8 h-8 flex-shrink-0">
            <Sun className="w-4 h-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute w-4 h-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-36">
          <DropdownMenuItem onClick={() => setTheme('light')} className={cn(theme === 'light' && 'text-lava')}>
            <Sun className="w-3.5 h-3.5 mr-2" /> Light
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('dark')} className={cn(theme === 'dark' && 'text-lava')}>
            <Moon className="w-3.5 h-3.5 mr-2" /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('system')} className={cn(theme === 'system' && 'text-lava')}>
            <Monitor className="w-3.5 h-3.5 mr-2" /> System
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
